import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Navbar from "components/navbar";
import Sidebar from "components/sidebar";
import Footer from "components/footer/Footer";
import AppUpdatePrompt from "components/AppUpdatePrompt";
import NewOrderArrivalOverlay from "components/orders/NewOrderArrivalOverlay";
import PosNotificationOnboarding from "components/PosNotificationOnboarding";
import routes from "routes.js";
import { useAuth } from "contexts/AuthContext";
import {
  startNewOrderSound,
  stopNewOrderSound,
  unlockNewOrderSound,
} from "hooks/useNewOrderAlert";
import { subscribeToRestaurantOrders } from "utils/pusher";

export default function Admin(props) {
  const { ...rest } = props;
  const { restaurantId } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(true);
  const [currentRoute, setCurrentRoute] = React.useState("Main Dashboard");
  const [newOrder, setNewOrder] = React.useState(null);
  const [audioBlocked, setAudioBlocked] = React.useState(false);
  const lastOrderAlertRef = React.useRef(null);

  React.useEffect(() => {
    const handleResize = () =>
      window.innerWidth < 1200 ? setOpen(false) : setOpen(true);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  React.useEffect(() => {
    const unlockAudio = () => {
      unlockNewOrderSound();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);
  React.useEffect(() => {
    getActiveRoute(routes);
  }, [location.pathname]);
  React.useEffect(() => {
    if (!restaurantId) {
      console.warn("[Pusher] restaurantId absent, abonnement ignore");
      return undefined;
    }

    return subscribeToRestaurantOrders(restaurantId, (data) => {
      if (Number(data?.restaurant_id) !== Number(restaurantId)) {
        console.warn("[Pusher] evenement ignore pour un autre restaurant", {
          expected: restaurantId,
          received: data?.restaurant_id,
        });
        return;
      }

      const alertKey = `${data?.id || data?.order_id}:${data?.source || data?.notification_source || "new_order"}`;
      if (lastOrderAlertRef.current === alertKey) return;
      lastOrderAlertRef.current = alertKey;
      console.log("[Pusher] nouvelle commande partenaire traitee", data);
      setNewOrder(data);
      setAudioBlocked(false);
      startNewOrderSound().then((played) => {
        if (played === false) setAudioBlocked(true);
      });
      window.dispatchEvent(new CustomEvent("restaurant:new-order", { detail: data }));
    });
  }, [restaurantId]);
  React.useEffect(() => {
    if (!("serviceWorker" in navigator) || !restaurantId) return undefined;
    const handleWorkerMessage = (event) => {
      if (event.data?.type !== "LIVRESTO_NEW_ORDER") return;
      const data = event.data.payload || {};
      if (Number(data.restaurant_id) !== Number(restaurantId)) return;
      const alertKey = `${data.order_id || data.id}:${data.source || "new_order"}`;
      if (lastOrderAlertRef.current === alertKey) return;
      lastOrderAlertRef.current = alertKey;
      setNewOrder({ ...data, id: data.order_id || data.id });
      setAudioBlocked(false);
      startNewOrderSound().then((played) => { if (played === false) setAudioBlocked(true); });
      window.dispatchEvent(new CustomEvent("restaurant:new-order", { detail: data }));
    };
    navigator.serviceWorker.addEventListener("message", handleWorkerMessage);
    return () => navigator.serviceWorker.removeEventListener("message", handleWorkerMessage);
  }, [restaurantId]);

  const dismissNewOrder = () => {
    stopNewOrderSound();
    setNewOrder(null);
  };

  const openNewOrder = () => {
    stopNewOrderSound();
    setNewOrder(null);
    navigate("/admin/orders");
  };

  const enableSound = () => {
    setAudioBlocked(false);
    startNewOrderSound().then((played) => {
      if (played === false) setAudioBlocked(true);
    });
  };

  const getActiveRoute = (routes) => {
    let activeRoute = "Main Dashboard";
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(
          routes[i].layout + "/" + routes[i].path
        ) !== -1
      ) {
        setCurrentRoute(routes[i].name);
      }
    }
    return activeRoute;
  };
  const getActiveNavbar = (routes) => {
    let activeNavbar = false;
    for (let i = 0; i < routes.length; i++) {
      if (
        window.location.href.indexOf(routes[i].layout + routes[i].path) !== -1
      ) {
        return routes[i].secondary;
      }
    }
    return activeNavbar;
  };
  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/admin") {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      } else {
        return null;
      }
    });
  };

  document.documentElement.dir = "ltr";
  return (
    <div className="flex h-full w-full">
      <NewOrderArrivalOverlay
        order={newOrder}
        audioBlocked={audioBlocked}
        onEnableSound={enableSound}
        onOpen={openNewOrder}
        onDismiss={dismissNewOrder}
      />
      <AppUpdatePrompt defer={Boolean(newOrder)} />
      <PosNotificationOnboarding />
      <Sidebar open={open} onClose={() => setOpen(false)} />
      {/* Navbar & Main Content */}
      <div className="h-full w-full bg-lightPrimary dark:!bg-navy-900">
        {/* Main Content */}
        <main
          className={`mx-[12px] h-full flex-none transition-all md:pr-2 xl:ml-[313px]`}
        >
          {/* Routes */}
          <div className="h-full">
            <Navbar
              onOpenSidenav={() => setOpen(true)}
              logoText={"Horizon UI Tailwind React"}
              brandText={currentRoute}
              secondary={getActiveNavbar(routes)}
              {...rest}
            />
            <div className="pt-5s mx-auto mb-auto h-full min-h-[84vh] p-2 md:pr-2">
              <Routes>
                {getRoutes(routes)}

                <Route
                  path="/"
                  element={<Navigate to="/admin/orders" replace />}
                />
              </Routes>
            </div>
            <div className="p-3">
              <Footer />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
